import {
  transformApplicableBudget,
  transformApplicationTemplate,
  transformBudgetApplication,
  transformBudgetEmployee,
  transformBudgetEmployeeChoice,
  transformBudgetExpense,
  transformBudgetMember,
  transformBudgetMemberInfo,
  transformBudgetSummary,
  transformBudgets,
} from '@/common/integrations/menhir/employee/budgets/budgets.dto';
import {
  BudgetApplicationAnswers,
  BudgetApplicationDynamicFormFields,
  BudgetApplicationFileUploadItem,
  BudgetFamilyMemberData,
  BudgetsStatus,
  ChildcareVoucherParams,
  IBudgetEmployeeChoice,
  IBudgetMemberInfo,
  IBudgetSummary,
  IEmployeeBudget,
  IMenhirApplicableBudget,
  IMenhirBudgetApplication,
  IMenhirBudgetApplicationTemplateEnvelope,
  IMenhirBudgetEmployeeChoice,
  IMenhirBudgetExpense,
  IMenhirBudgetMemberInfo,
  IMenhirBudgetSummaryEnvelope,
  IMenhirBudgetsEnvelope,
  IMenhirEmployeeBudget,
  IMenhirEmployeeBudgetEnvelope,
} from '@/common/integrations/menhir/employee/budgets/budgets.types';
import { menhirClient } from '@/common/integrations/menhir/menhir.client';
import {
  MENHIR_ENDPOINTS,
  endpointFor,
} from '@/common/integrations/menhir/menhir.endpoints';
import { PaginationResponse, TaxId } from '@/common/types/general';
import { IMenhirPaginationResponse } from '@/common/types/menhir/general';
import { getFormDataFromObject } from '@/common/utils/form';
import { ArrayUtils } from '@coverflex-tech/hypernova-utils';
import { AxiosRequestConfig } from 'axios';
import { BudgetsStorage } from './budgets.storage';

const getBudgets = (
  statusIn: Array<BudgetsStatus> = ['active', 'expired', 'removed']
): Promise<IEmployeeBudget[]> =>
  menhirClient
    .get<null, IMenhirBudgetsEnvelope>(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.ROOT, {
      params: {
        format: 'full',
        'filters[status_in]': statusIn,
        pagination: 'no',
      },
    })
    .then((envelope) => {
      const budgets = transformBudgets(envelope.budget_employees).list;
      BudgetsStorage.updateActiveBudgetsCount(budgets);
      return budgets;
    });

const getEmployeeBudgetChoices = (): Promise<IBudgetEmployeeChoice[]> => {
  return menhirClient
    .get<
      null,
      {
        budget_employees: PaginationResponse<IMenhirBudgetEmployeeChoice>;
      }
    >(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.ROOT, {
      params: {
        format: 'choice',
        'filters[status_in]': ['active'],
        pagination: 'no',
      },
    })
    .then(({ budget_employees: { list } }) =>
      list.map(transformBudgetEmployeeChoice)
    );
};

const getBudgetEmployeeDetail = (budgetId: string): Promise<IEmployeeBudget> =>
  menhirClient
    .get<
      null,
      IMenhirEmployeeBudgetEnvelope
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.DETAIL, budgetId))
    .then((envelope) => {
      return transformBudgetEmployee(envelope.budget_employee);
    });

const getBudgetEmployees = (budgetId: string): Promise<IEmployeeBudget[]> =>
  menhirClient
    .get<
      null,
      { budget_employees: IMenhirPaginationResponse<IMenhirEmployeeBudget> }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.BUDGET_EMPLOYEES.ROOT, budgetId))
    .then(({ budget_employees }) => {
      return budget_employees.list.map(transformBudgetEmployee);
    });

const getBudgetsSummary = (): Promise<IBudgetSummary> =>
  menhirClient
    .get<null, IMenhirBudgetSummaryEnvelope>(
      MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.ROOT,
      {
        params: {
          format: 'summary',
          'filters[status_in]': ['active', 'expired', 'removed'],
          pagination: 'no',
        },
      }
    )
    .then(({ budget_employees }) => {
      return transformBudgetSummary(budget_employees);
    });

const getBudgetEmployeeExpense = ({
  budgetId,
  budgetEmployeeId,
  expenseId,
}: {
  budgetId: string;
  budgetEmployeeId: string;
  expenseId: string;
}) =>
  menhirClient
    .get<
      null,
      { budget_expense: IMenhirBudgetExpense }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.BUDGET_EMPLOYEES.EXPENSES.DETAIL, budgetId, budgetEmployeeId, expenseId))
    .then(({ budget_expense }) => transformBudgetExpense(budget_expense));

const getApplicableBudgets = () =>
  menhirClient
    .get<
      null,
      {
        applicable_budgets: IMenhirPaginationResponse<IMenhirApplicableBudget>;
      }
    >(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.ROOT, {
      params: {
        applicable: true,
      },
    })
    .then(({ applicable_budgets }) => {
      return applicable_budgets.list.map((applicableBudgets) => {
        return transformApplicableBudget(applicableBudgets);
      });
    });

const getBudgetApplications = (budgetId?: string) =>
  menhirClient
    .get<
      null,
      { applications: IMenhirPaginationResponse<IMenhirBudgetApplication> }
    >(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.APPLICATIONS.ROOT, budgetId ? { params: { 'filters[budget_id]': budgetId } } : undefined)
    .then(({ applications }) => {
      return applications.list.map(transformBudgetApplication);
    });

const getBudgetApplicationTemplate = (budgetId: string) =>
  menhirClient
    .get<
      null,
      IMenhirBudgetApplicationTemplateEnvelope
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.BUDGET_APPLICATIONS.TEMPLATE, budgetId))
    .then(({ application_template, applications_count }) => {
      return transformApplicationTemplate(
        application_template,
        applications_count
      );
    });

const uploadApplicationFiles = (
  fileData: FormData
): Promise<BudgetApplicationFileUploadItem[]> => {
  return menhirClient
    .post<
      FormData,
      {
        files: Array<{
          file_id: string;
          field_id: string;
        }>;
      }
    >(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.APPLICATIONS.FILES, fileData)
    .then(({ files }) => {
      return files.map((f) => ({
        fieldId: f.field_id,
        fileId: f.file_id,
      }));
    });
};

const submitBudgetApplication = async ({
  budgetId,
  fields,
}: {
  budgetId: string;
  fields: BudgetApplicationDynamicFormFields;
}) => {
  let uploadedFiles: BudgetApplicationFileUploadItem[] = [];
  const attachments = fields.attachments.filter((f) => !!f.blob);
  if (attachments.length > 0) {
    const fileData = getFormDataFromObject({
      files: attachments.reduce((currentObj, attachment) => {
        if (attachment.id) {
          return {
            ...currentObj,
            [attachment.id]: attachment.blob,
          };
        }
        return currentObj;
      }, {}),
    });
    uploadedFiles = await uploadApplicationFiles(fileData);
  }

  const answers = Object.entries(fields).reduce(
    (responseArray, [key, currentResponse]) => {
      // name is returned separately
      if (key === 'name') {
        return responseArray;
      }

      // attachments are referenced by file id
      if (key === 'attachments' && Array.isArray(currentResponse)) {
        const fileAnswers = currentResponse
          .map((responseItem) => {
            const file = uploadedFiles.find(
              (f) => f.fieldId === responseItem.id
            );
            if (file) {
              return { field_id: file.fieldId, answer: file.fileId };
            }
            return null;
          })
          .filter(ArrayUtils.removeFalsey);

        return [...responseArray, ...fileAnswers];
      }

      return [...responseArray, { field_id: key, answer: currentResponse }];
    },
    [] as Array<{ field_id: string; answer: string }>
  );

  return menhirClient.post<BudgetApplicationAnswers>(
    endpointFor(
      MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.BUDGET_APPLICATIONS.APPLY,
      budgetId
    ),
    { answers, name: fields.name }
  );
};

const getBudgetApplication = (budgetId: string, applicationId: string) => {
  return menhirClient
    .get<
      null,
      { application: IMenhirBudgetApplication }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.BUDGET_APPLICATIONS.DETAIL, budgetId, applicationId))
    .then(({ application }) => {
      return transformBudgetApplication(application);
    });
};

const cancelBudgetApplication = ({
  budgetId,
  applicationId,
}: {
  budgetId: string;
  applicationId: string;
}) => {
  return menhirClient.post(
    endpointFor(
      MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.BUDGET_APPLICATIONS.CANCEL,
      budgetId,
      applicationId
    )
  );
};

const getFamilyMember = (
  budgetEmployeeId: string
): Promise<IBudgetMemberInfo> => {
  return menhirClient
    .get<
      null,
      IMenhirBudgetMemberInfo
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.FAMILY_MEMBER, budgetEmployeeId))
    .then((familyMember) => {
      return transformBudgetMemberInfo(familyMember);
    });
};

const setBudgetFamilyMember = ({
  budgetEmployeeId,
  member,
}: {
  budgetEmployeeId: string;
  member: BudgetFamilyMemberData;
}): Promise<IBudgetMemberInfo['member']> => {
  return menhirClient
    .post<
      null,
      { member: { name: string; tax_id: TaxId } },
      BudgetFamilyMemberData
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.FAMILY_MEMBER, budgetEmployeeId), member)
    .then(({ member: response }) => {
      return transformBudgetMember(response);
    });
};

const submitChildcareVoucher = (
  { budgetEmployeeId, amount, schoolId }: ChildcareVoucherParams,
  config?: AxiosRequestConfig
) => {
  return menhirClient.post(
    endpointFor(
      MENHIR_ENDPOINTS.EMPLOYEE.BUDGETS.VOUCHERS.CHILDCARE,
      budgetEmployeeId
    ),
    {
      amount: {
        amount: amount.amount,
        currency: amount.currency,
      },
      school_id: schoolId,
    },
    config
  );
};

export const BudgetsService = {
  getBudgets,
  getBudgetEmployeeDetail,
  getBudgetsSummary,
  getBudgetEmployeeExpense,
  getEmployeeBudgetChoices,
  getBudgetEmployees,
  getApplicableBudgets,
  getBudgetApplication,
  getBudgetApplications,
  getBudgetApplicationTemplate,
  uploadApplicationFiles,
  submitBudgetApplication,
  cancelBudgetApplication,
  getFamilyMember,
  setBudgetFamilyMember,
  submitChildcareVoucher,
};
